import { useEffect, useMemo, useState } from "react";
import { api, unwrap } from "../api/client";
import type { GwEvent, StateMessage } from "../api/types";
import { Panel, PageHeader, Pill, Segmented, TextInput, type Tone } from "../components/ui";
import { formatDuration, formatTime } from "../lib/format";
import { N_, serverText, t } from "../lib/i18n";
import { mergeEvents, useLive } from "../lib/live";
import { href } from "../lib/router";

const LEVEL_TONE: Record<GwEvent["level"], Tone> = { info: "muted", warning: "warn", error: "err" };
const LEVEL_LABEL: Record<GwEvent["level"], string> = { info: N_("Info"), warning: N_("Alerte"), error: N_("Erreur") };

function SourceLink({ source, state }: { source: string; state: StateMessage }) {
  const [kind, id] = source.split(":");
  if (kind === "input" && id) {
    const name = state.inputs.find((i) => i.id === id)?.name ?? id;
    return <a className="hover:text-accent" href={href({ page: "input", id })}>{name}</a>;
  }
  if (kind === "output" && id) {
    const name = state.outputs.find((o) => String(o.id) === id)?.name ?? t("sortie {id}", { id });
    return <a className="hover:text-accent" href={href({ page: "outputs" })}>{name}</a>;
  }
  return <>{kind === "auth" ? t("Comptes") : t("Système")}</>;
}

export function EventRow({ event, state, hideSource = false }: { event: GwEvent; state: StateMessage; hideSource?: boolean }) {
  return (
    <tr className="align-top">
      <td className="num w-24 px-4 py-2 font-mono text-[12px] whitespace-nowrap text-faint">{formatTime(event.ts)}</td>
      <td className="w-24 px-2 py-2"><Pill tone={LEVEL_TONE[event.level]}>{t(LEVEL_LABEL[event.level])}</Pill></td>
      {!hideSource && <td className="w-44 px-2 py-2 text-[13px] text-muted"><SourceLink source={event.source} state={state} /></td>}
      <td className="px-4 py-2">{serverText(event.message)}</td>
      <td className="w-32 px-4 py-2 text-right text-[12px] whitespace-nowrap text-muted">
        {event.user && <span title={t("Auteur de l'action")}>{t("par {user}", { user: event.user })}</span>}
      </td>
    </tr>
  );
}

type LevelFilter = "all" | "warning" | "error" | "audit";

export function Journal({ state }: { state: StateMessage }) {
  const { events } = useLive();
  const [past, setPast] = useState<GwEvent[]>([]);
  const [level, setLevel] = useState<LevelFilter>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    unwrap(api.GET("/api/events", { params: { query: { limit: 500 } } })).then(setPast).catch(() => setPast([]));
  }, []);

  const all = useMemo(() => mergeEvents(past, [...events].reverse()), [past, events]);
  const q = search.toLowerCase();
  const shown = all.filter((e) =>
    (level === "all" || (level === "audit" ? e.user !== null : level === "warning" ? e.level !== "info" : e.level === "error")) &&
    (!q || e.message.toLowerCase().includes(q) || serverText(e.message).toLowerCase().includes(q) || e.source.includes(q) || (e.user ?? "").toLowerCase().includes(q)));
  const now = state.ts;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Alarmes et journal")} />

      <Panel title={t("Alarmes actives ({n})", { n: state.alarms.length })}>
        {state.alarms.length === 0 ? <p className="p-4 text-ok">{t("Aucune alarme : tout fonctionne.")}</p> : (
          <ul className="divide-y divide-line">
            {state.alarms.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <Pill tone={a.level === "warning" ? "warn" : "err"}>{a.level === "critical" ? t("Critique") : a.level === "error" ? t("Erreur") : t("Alerte")}</Pill>
                <span className="min-w-0 flex-1">{serverText(a.message)}</span>
                <span className="text-[13px] text-muted"><SourceLink source={a.source} state={state} /></span>
                <span className="num font-mono text-[12px] text-faint">{t("depuis {duration}", { duration: formatDuration(now - a.since) })}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={t("Journal")}
        actions={<div className="flex flex-wrap items-center gap-2">
          <Segmented label={t("Niveau")} value={level} onChange={setLevel}
            options={[{ value: "all", label: t("Tout") }, { value: "warning", label: t("Alertes et erreurs") },
              { value: "error", label: t("Erreurs") }, { value: "audit", label: t("Actions des utilisateurs") }]} />
          <TextInput aria-label={t("Rechercher")} placeholder={t("Rechercher…")} value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-48" />
        </div>}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <tbody className="divide-y divide-line">
              {shown.map((e) => <EventRow key={e.id} event={e} state={state} />)}
            </tbody>
          </table>
          {shown.length === 0 && <p className="p-4 text-muted">{t("Aucun événement ne correspond.")}</p>}
        </div>
      </Panel>
    </div>
  );
}
